import React, { BaseSyntheticEvent, FC, ReactNode } from 'react';

import {
  Box,
  Center,
  Table,
  TableContainer,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
} from '@chakra-ui/react';
import { isEmpty, map } from 'lodash';
import { customTheme } from 'theme';

import { NoDataAvailable } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';

interface Props {
  columns: {
    header: ReactNode;
    accessor?: string;
    render?({ row, value });
    styles?: {
      td?: Record<string, any>;
      cell?: Record<string, any>;
      header?: Record<string, any>;
      w?: string | number;
    };
  }[];
  data: Record<string, any>[];
  isLoading?: boolean;
  noDataMessage?: ReactNode;
  align?: any;
  onRowClick?(row);
  styles?: { td?: Record<string, any>; cell?: Record<string, any> };
  onScroll?(e: BaseSyntheticEvent);
  onScrollIsLoading?: boolean;
}

export const DashboardTable: FC<Props> = props => {
  const {
    columns,
    data,
    isLoading,
    noDataMessage,
    align = 'left',
    styles,
    onRowClick,
    onScroll,
    onScrollIsLoading,
  } = props;
  return (
    <TableContainer
      h="full"
      w="full"
      maxH="full"
      maxW="full"
      minH={0}
      minW={0}
      overflowY="auto"
      overflowX="hidden"
    >
      <Table
        variant="unstyled"
        h={isLoading || isEmpty(data) ? 'full' : 'default'}
        w="full"
        layout="fixed"
      >
        <Thead position="sticky" top={0} zIndex={1} bg="white">
          <Tr borderBottom="none" w="full">
            {map(columns, (o, index) => (
              <Th
                fontWeight={400}
                fontFamily="Rubik"
                color={customTheme.colors.gray['270']}
                fontSize="13px"
                border="0"
                textAlign={align}
                px={3}
                whiteSpace="nowrap"
                key={index + 'dashboard-table-header'}
                w={o?.styles?.w}
                {...o.styles?.header}
              >
                {o.header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody h="full" overflow="scroll" onScroll={onScroll}>
          {isLoading || isEmpty(data) ? (
            <Tr h="full">
              <Td colSpan={columns?.length} borderBottom="0">
                <Box h="full" display="flex" alignItems="center">
                  <Center w="full" h="full">
                    {isLoading ? (
                      <CustomSpinner />
                    ) : (
                      <NoDataAvailable text={noDataMessage} />
                    )}
                  </Center>
                </Box>
              </Td>
            </Tr>
          ) : (
            map(data, (row, index) => (
              <Tr
                borderRadius={6}
                _hover={{ bg: 'selectedSideNavMenuBg' }}
                w="full"
                css={{
                  'td: first-child': { borderLeftRadius: 6 },
                  'td: last-child': { borderRightRadius: 6 },
                }}
                cursor={onRowClick ? 'pointer' : 'inherit'}
                onClick={() => onRowClick?.(row)}
                key={index + '-dashboard-table-row'}
              >
                {map(columns, (col, index) => (
                  <Td
                    px={3}
                    py={3}
                    borderLeftRadius={index === 0 ? 6 : 0}
                    borderRightRadius={index === columns?.length - 1 ? 6 : 0}
                    w={col?.styles?.w}
                    key={index + '-dashboard-table-cell'}
                    {...styles?.td}
                    {...col?.styles?.td}
                  >
                    <Box
                      textAlign={align}
                      w="full"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      {...styles?.cell}
                      {...col?.styles?.cell}
                    >
                      {col?.render
                        ? col?.render?.({
                            row,
                            value: col?.accessor ? row[col?.accessor] : '',
                          })
                        : col?.accessor
                        ? row[col?.accessor]
                        : ''}
                    </Box>
                  </Td>
                ))}
              </Tr>
            ))
          )}
          {onScrollIsLoading && (
            <Tr>
              <CustomSpinner />
            </Tr>
          )}
        </Tbody>
        {/*)}*/}
      </Table>
    </TableContainer>
  );
};
